import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DbPortalChecklistePunkt, DbPortalStandortDetail } from '@gql/shared-types';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { FortschrittBalken, PrioMarke } from '../components/Fortschritt.js';
import { LieferungenEinesStandorts } from '../components/Lieferungen.js';
import { StandortNutzer } from '../components/StandortNutzer.js';
import { UploadBereich } from '../components/UploadBereich.js';
import { firmenNamen, konzessionaerText } from '../daten/ansicht.js';
import { useDatenKontext } from '../daten/kontext.js';
import { datumText, euro, KOMPONENTEN_TEXT, komponentenUebersicht, prozentText, referenzFuer, vielfachesText } from '../daten/lieferungen.js';
import { adresse, punktTitel, STATUS_TEXT, ZUORDNUNG_TEXT } from '../daten/standorte.js';
import { KERN_BIS, KERN_VON } from '../daten/zeitreihe.js';

export function StandortDetail() {
  const { id } = useParams();
  const daten = useDatenKontext();
  const { zustand } = useAuth();
  const admin = zustand.status === 'bereit' && zustand.ich.nutzer?.rolle === 'gql_admin';
  const [s, setS] = useState<DbPortalStandortDetail | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);

  const ladeStandort = useCallback(() => {
    api<DbPortalStandortDetail>(`/standorte/${id}`)
      .then(setS)
      .catch((f: Error) => setFehler(f.message));
  }, [id]);
  useEffect(ladeStandort, [ladeStandort]);

  if (fehler && !s) {
    return (
      <div className="seite">
        <p className="brotkrumen">
          <Link to="/standorte">← Zu den Standorten</Link>
        </p>
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      </div>
    );
  }
  if (!s) {
    return (
      <div className="seite" role="status">
        <span className="spinner" aria-hidden="true" /> Standort wird geladen …
      </div>
    );
  }

  const gebiet = s.gebietSlug ? daten.gebiete.features.find((f) => f.properties.slug === s.gebietSlug) : undefined;
  const vorschlag = s.zuordnungVorschlagSlug ? daten.gebiete.features.find((f) => f.properties.slug === s.zuordnungVorschlagSlug) : undefined;
  const namen = firmenNamen(daten);
  const firmenImKern = gebiet
    ? [...new Set(gebiet.properties.zeitreihe.filter((e) => e.von <= `${KERN_BIS}-12-31` && (e.bis === null || e.bis > `${KERN_VON}-01-01`)).flatMap((e) => e.k))]
    : [];

  async function setzeStatus(p: DbPortalChecklistePunkt, status: DbPortalChecklistePunkt['status']) {
    const schluessel = `${p.art}:${p.jahr}`;
    setSpeichert(schluessel);
    setFehler(null);
    try {
      setS(await api<DbPortalStandortDetail>(`/standorte/${id}/checkliste`, { methode: 'PUT', json: { art: p.art, jahr: p.jahr, status, bemerkung: p.bemerkung } }));
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setSpeichert(null);
    }
  }

  async function adminAendern(aenderung: { gebietSlug?: string | null; prioritaet?: number | null }) {
    try {
      setS(await api<DbPortalStandortDetail>(`/admin/standorte/${id}`, { methode: 'PATCH', json: aenderung }));
    } catch (f) {
      setFehler((f as Error).message);
    }
  }

  return (
    <article className="seite">
      <p className="brotkrumen">
        <Link to="/standorte">← Zu den Standorten</Link>
      </p>
      <header className="gebiet-kopf">
        <h1>
          <PrioMarke p={s.prioritaet} /> {s.name}
        </h1>
        <p className="gebiet-unterzeile">
          {adresse(s)}
          {s.externeId && ` · ID ${s.externeId}`}
        </p>
      </header>

      <div className="kennzahlen">
        <div className="kennzahl">
          <span className="kennzahl-label">Konzessionsgebiet ({ZUORDNUNG_TEXT[s.zuordnungArt]})</span>
          <span className="kennzahl-wert">
            {gebiet ? <Link to={`/gebiet/${gebiet.properties.slug}`}>{gebiet.properties.name}</Link> : 'noch offen'}
          </span>
          {!gebiet && vorschlag && (
            <span className="zelle-hinweis">
              Vorschlag: {vorschlag.properties.name} ({s.zuordnungEntfernungKm} km entfernt) – GQL prüft die Zuordnung.
            </span>
          )}
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">
            Konzessionär {KERN_VON}–{KERN_BIS}
          </span>
          <span className="kennzahl-wert">{firmenImKern.length ? konzessionaerText(firmenImKern, namen) : '–'}</span>
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">Unterlagen</span>
          <span className="kennzahl-wert">
            <FortschrittBalken f={s.fortschritt} />
          </span>
        </div>
        <div className="kennzahl">
          <span className="kennzahl-label">Bericht</span>
          <span className="kennzahl-wert">
            <Link to={`/standorte/${s.id}/bericht`}>Standortbericht und Entgeltreihe</Link>
          </span>
          <span className="zelle-hinweis">Entgelte im Zeitverlauf, Belege, Einschätzungsreife{admin && ', Verjährungsfenster, Schätzung'}</span>
        </div>
      </div>

      {admin && (
        <section className="kasten" aria-labelledby="h-verwaltung">
          <h2 id="h-verwaltung">Verwaltung (nur GQL)</h2>
          <div className="filterzeile">
            <label className="feld">
              <span className="feld-label">Gebiet zuordnen</span>
              <select value={s.gebietSlug ?? ''} onChange={(e) => void adminAendern({ gebietSlug: e.target.value || null })}>
                <option value="">– offen –</option>
                {daten.gebiete.features
                  .map((f) => f.properties)
                  .sort((a, b) => a.name.localeCompare(b.name, 'de'))
                  .map((g) => (
                    <option key={g.slug} value={g.slug}>
                      {g.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="feld">
              <span className="feld-label">Priorität</span>
              <select value={s.prioritaet ?? ''} onChange={(e) => void adminAendern({ prioritaet: e.target.value ? Number(e.target.value) : null })}>
                <option value="">–</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </label>
          </div>
          <StandortNutzer standortId={s.id} />
        </section>
      )}

      <section aria-labelledby="h-checkliste">
        <h2 id="h-checkliste">Benötigte Unterlagen</h2>
        <p className="hinweis-klein">
          Mindestpaket: ein Vertrag und je Jahr {KERN_VON}–{KERN_BIS} eine Rechnung. Hochgeladene Unterlagen werden
          automatisch erkannt und abgehakt. Liegt ein Stück nicht vor, markieren Sie es bitte – auch das hilft uns weiter.
        </p>
        {fehler && (
          <p className="fehlertext" role="alert">
            {fehler}
          </p>
        )}
        <ul className="checkliste">
          {s.checkliste.map((p) => {
            const schluessel = `${p.art}:${p.jahr}`;
            const nichtVorhanden = p.status === 'nicht_vorhanden';
            const durchGql = p.status === 'geliefert' || p.status === 'geprueft';
            return (
              <li key={schluessel} className={`checkliste-punkt status-${p.status}`}>
                <span className="checkliste-zeichen" aria-hidden="true">
                  {durchGql ? '✓' : nichtVorhanden ? '–' : '○'}
                </span>
                <span className="checkliste-titel">
                  {punktTitel(p)}
                  {!p.pflicht && <span className="zelle-hinweis"> (erwünscht)</span>}
                </span>
                <span className="checkliste-status">
                  {STATUS_TEXT[p.status]}
                  {p.quartale && p.quartale.length > 0 && (
                    <span className="checkliste-quartale" title="Quartale, die gelieferte Rechnungen abdecken">
                      {p.quartale.length === 4 ? 'alle Quartale' : p.quartale.map((q) => `Q${q}`).join(', ')}
                    </span>
                  )}
                </span>
                {!durchGql && (
                  <button
                    type="button"
                    className="knopf-klein-text"
                    disabled={speichert === schluessel}
                    onClick={() => void setzeStatus(p, nichtVorhanden ? 'fehlt' : 'nicht_vorhanden')}
                    aria-label={`${punktTitel(p)}: ${nichtVorhanden ? 'doch vorhanden' : 'liegt nicht vor'}`}
                  >
                    {nichtVorhanden ? 'Doch vorhanden' : 'Liegt nicht vor'}
                  </button>
                )}
                {admin && p.status === 'geliefert' && (
                  <button type="button" className="knopf-klein-text" onClick={() => void setzeStatus(p, 'geprueft')}>
                    Als geprüft markieren
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {s.entgeltentwicklung && s.entgeltentwicklung.length > 0 && (
        <section aria-labelledby="h-entgelte">
          <h2 id="h-entgelte">Entgeltentwicklung</h2>
          <p className="zelle-hinweis">Aus den gelieferten Mitteilungen über geänderte Entgelte (netto, Gesamtbetrag; monatlich, sofern nicht anders vermerkt); „davon Sicherheits-/Zeitsysteme“ = Anteil des Konzessionärs ohne durchgereichte Leitungsgebühren, sofern ausgewiesen – automatische Auswertung, maßgeblich sind die Originale.</p>
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-klein tabelle-karten">
              <caption className="sr-only">Entgeltentwicklung</caption>
              <thead>
                <tr>
                  <th scope="col">Gültig ab</th>
                  <th scope="col">Bisher</th>
                  <th scope="col">Neu</th>
                  <th scope="col">Erhöhung</th>
                  <th scope="col">davon Sicherheits-/Zeitsysteme</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {s.entgeltentwicklung.map((e) => (
                  <tr key={e.dokumentId}>
                    <td data-label="Gültig ab">{e.gueltigAb ? datumText(e.gueltigAb) : '–'}</td>
                    <td data-label="Bisher" className="zahl">
                      {e.bisherCent !== null ? euro(e.bisherCent) : '–'}
                    </td>
                    <td data-label="Neu" className="zahl">
                      {e.neuCent !== null ? `${euro(e.neuCent)}${e.zeitraum ? ` je ${e.zeitraum === 'quartal' ? 'Quartal' : 'Jahr'}` : ''}` : '–'}
                    </td>
                    <td data-label="Erhöhung" className="zahl">
                      {e.erhoehungCent !== null ? `${euro(e.erhoehungCent)}${e.prozent !== null ? ` (${prozentText(e.prozent)})` : ''}` : '–'}
                    </td>
                    <td data-label="davon Sicherheits-/Zeitsysteme" className="zahl">
                      {e.anteilNeuCent !== null && e.anteilNeuCent !== undefined
                        ? `${euro(e.anteilNeuCent)}${e.anteilProzent !== null && e.anteilProzent !== undefined ? ` (${prozentText(e.anteilProzent)})` : ''}`
                        : '–'}
                    </td>
                    <td data-label="Stand">
                      {[e.uebernommen ? 'übernommen' : 'in Prüfung', e.stimmig ? 'rechnerisch stimmig' : 'bitte prüfen'].join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {s.komponentenpreise && s.komponentenpreise.length > 0 && (
        <section aria-labelledby="h-komponenten">
          <h2 id="h-komponenten">Komponentenpreise</h2>
          <p className="zelle-hinweis">
            Aus Preistabellen der gelieferten Unterlagen (z. B. Bosch „Menge | Miete monatlich“); gleicher Preis aus mehreren Unterlagen in einer Zeile – automatische Auswertung, maßgeblich sind die Originale. „Kaufpreis (Referenz)“ ist der Netto-Kaufpreis eines Neugeräts laut angegebener Quelle; „Miete in 10 Jahren ÷ Kaufpreis“ setzt die Monatsmiete je Stück über 120 Monate dazu ins Verhältnis.
          </p>
          <div className="tabelle-rahmen">
            <table className="tabelle tabelle-klein tabelle-karten">
              <caption className="sr-only">Komponentenpreise</caption>
              <thead>
                <tr>
                  <th scope="col">Komponente</th>
                  <th scope="col">Miete monatlich je Stück</th>
                  <th scope="col">Einrichtung</th>
                  <th scope="col">Kaufpreis (Referenz)</th>
                  <th scope="col">Miete in 10 Jahren ÷ Kaufpreis</th>
                  <th scope="col">Unterlagen</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {komponentenUebersicht(s.komponentenpreise).map((k, i) => (
                  <tr key={i}>
                    <td data-label="Komponente">
                      {k.bezeichnung}
                      <span className="zelle-hinweis"> · {KOMPONENTEN_TEXT[k.art]}</span>
                    </td>
                    <td data-label="Miete monatlich je Stück" className="zahl">
                      {k.preis === 'im_paket'
                        ? k.paket
                          ? `im Paketpreis „${k.paket}“`
                          : 'im Gesamtpreis enthalten'
                        : k.einzelpreisCent !== null
                          ? euro(k.einzelpreisCent)
                          : k.mieteCent !== null
                            ? `${euro(k.mieteCent)} (Zeile)`
                            : 'nicht lesbar'}
                    </td>
                    <td data-label="Einrichtung" className="zahl">
                      {k.einrichtungCent !== null ? euro(k.einrichtungCent) : '–'}
                    </td>
                    <td data-label="Kaufpreis (Referenz)" className="zahl">
                      {referenzFuer(k.art, s.referenzpreise) ? (
                        <>
                          {euro(referenzFuer(k.art, s.referenzpreise)!.preisCent)}
                          <span className="zelle-hinweis">
                            {' '}
                            {referenzFuer(k.art, s.referenzpreise)!.typ ?? ''}
                            {referenzFuer(k.art, s.referenzpreise)!.vergleichsklasse ? ' (Vergleichsklasse)' : ''} · Stand {datumText(referenzFuer(k.art, s.referenzpreise)!.stand)}
                          </span>
                        </>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td data-label="Miete in 10 Jahren ÷ Kaufpreis" className="zahl">
                      {vielfachesText(k, referenzFuer(k.art, s.referenzpreise)) ?? '–'}
                    </td>
                    <td data-label="Unterlagen">
                      {k.von ? (k.von === k.bis ? datumText(k.von) : `${datumText(k.von)} – ${datumText(k.bis!)}`) : '–'}
                      {k.unterlagen > 1 ? ` (${k.unterlagen})` : ''}
                    </td>
                    <td data-label="Stand">{k.stimmig ? 'Summe stimmt' : 'bitte am Original prüfen'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const arten = new Set(komponentenUebersicht(s.komponentenpreise).map((k) => k.art));
            const quellen = (s.referenzpreise ?? []).filter((r) => arten.has(r.art));
            return quellen.length ? (
              <details className="quellen">
                <summary>Quellen der Referenz-Kaufpreise ({quellen.length})</summary>
                <ul>
                  {quellen.map((r, i) => (
                    <li key={i}>
                      {KOMPONENTEN_TEXT[r.art]}: {r.bezeichnung} – {euro(r.preisCent)} netto
                      {r.bruttoCent !== null ? ` (${euro(r.bruttoCent)} brutto)` : ''}, {r.quelle}, Stand {datumText(r.stand)}
                      {r.url ? (
                        <>
                          {' '}
                          <a href={r.url} target="_blank" rel="noreferrer">
                            Quelle öffnen
                          </a>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null;
          })()}
        </section>
      )}

      <section aria-labelledby="h-upload">
        <h2 id="h-upload">Unterlagen hochladen</h2>
        <UploadBereich standortId={s.id} onHochgeladen={() => setNeuLaden((n) => n + 1)} />
      </section>

      <section aria-labelledby="h-geliefert">
        <h2 id="h-geliefert">Gelieferte Unterlagen und Auswertung</h2>
        <LieferungenEinesStandorts standortId={s.id} neuLaden={neuLaden} beiAbschluss={ladeStandort} />
      </section>
    </article>
  );
}
